import { generateUUID } from "./uuid";

export const CAPTURE_ENDPOINT = "/api/capture";

export type CaptureOutcome =
  | "REJECTED_CLIENT_VALIDATION"
  | "TRANSPORT_FAILED"
  | "REJECTED_VALIDATION"
  | "REJECTED_ANTIBOT"
  | "ACCEPTED_QUEUED";

interface CaptureLeadAttemptParams {
  captureId: string;
  source: string;
  rawPayload: Record<string, any>;
  pageUrl?: string;
  formId?: string;
  visitorId?: string;
}

interface ReportCaptureOutcomeParams {
  captureId: string;
  outcome: CaptureOutcome;
  outcomeDetail?: string;
}

export interface CaptureOutcomeForStatus {
  outcome: CaptureOutcome;
  outcomeDetail?: string;
}

export function createCaptureId(): string {
  return generateUUID();
}

export function outcomeForStatus(status: number): CaptureOutcomeForStatus {
  if (status === 422) {
    return { outcome: "REJECTED_VALIDATION" };
  }

  if (status === 403) {
    return { outcome: "REJECTED_ANTIBOT" };
  }

  return { outcome: "TRANSPORT_FAILED", outcomeDetail: `HTTP ${status}` };
}

function trySendBeacon(payload: string): boolean {
  try {
    if (
      typeof navigator === "undefined" ||
      typeof navigator.sendBeacon !== "function"
    ) {
      return false;
    }

    return navigator.sendBeacon(
      CAPTURE_ENDPOINT,
      new Blob([payload], { type: "application/json" }),
    );
  } catch {
    return false;
  }
}

function serialize(body: Record<string, any>): string | null {
  try {
    return JSON.stringify(body);
  } catch {
    return null;
  }
}

function send(body: Record<string, any>): void {
  if (typeof window === "undefined") {
    return;
  }

  const payload = serialize(body);

  if (payload === null) {
    return;
  }

  if (trySendBeacon(payload)) {
    return;
  }

  if (typeof fetch !== "function") {
    return;
  }

  void fetch(CAPTURE_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => undefined);
}

export function captureLeadAttempt({
  captureId,
  source,
  rawPayload,
  pageUrl,
  formId,
  visitorId,
}: CaptureLeadAttemptParams): void {
  send({
    captureId,
    source,
    rawPayload,
    ...(pageUrl ? { pageUrl } : {}),
    ...(formId ? { formId } : {}),
    ...(visitorId ? { visitorId } : {}),
  });
}

export function reportCaptureOutcome({
  captureId,
  outcome,
  outcomeDetail,
}: ReportCaptureOutcomeParams): void {
  send({
    captureId,
    outcome,
    ...(outcomeDetail ? { outcomeDetail } : {}),
  });
}
